import React from 'react';
import { X, Copy } from 'lucide-react';
import { License } from '../types/License';
import dashboardApi from '../api/dashboardApi';
import { useNotification } from '../context/NotificationContext';
import './LicenseModal.css';

interface LicenseModalProps {
  license: License;
  onClose: () => void;
}

const formatWhen = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const truncate = (text: string, max = 36): string =>
  text.length > max ? `${text.slice(0, max)}…` : text;

const LicenseModal: React.FC<LicenseModalProps> = ({ license, onClose }) => {
  const { success } = useNotification();

  const copyToClipboard = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    success(`${label} copied`);
  };

  const expiryShort = license.expires_at
    ? formatWhen(license.expires_at).split(',')[0]
    : '—';

  const expiryLabel =
    license.days_remaining > 0
      ? `${license.days_remaining}d left`
      : 'Expired';

  const recentHistory = (license.request_history || []).slice(-3).reverse();

  return (
    <div className="lm-overlay" onClick={onClose}>
      <div className="lm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lm-header">
          <div className="lm-header-main">
            <div className="lm-title-row">
              <h2 className="lm-title">{license.org_name?.trim() || '—'}</h2>
              <span className={`lm-status ${license.status}`}>{license.status}</span>
            </div>
            <p className="lm-email" title={license.email}>{license.email}</p>
          </div>
          <button type="button" className="lm-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="lm-body">
          <p className="lm-hint">
            Edit cameras, platform &amp; UI settings in the <strong>Limits</strong> tab.
          </p>

          <div className="lm-stats">
            <div className="lm-stat">
              <span className="lm-stat-label">Expires</span>
              <span className="lm-stat-value">{expiryShort}</span>
            </div>
            <div className="lm-stat">
              <span className="lm-stat-label">Remaining</span>
              <span className={`lm-stat-value ${license.days_remaining > 7 ? 'ok' : 'danger'}`}>
                {expiryLabel}
              </span>
            </div>
            <div className="lm-stat">
              <span className="lm-stat-label">Auth</span>
              <span className="lm-stat-value">{license.auth_level || 'STANDARD'}</span>
            </div>
            <div className="lm-stat">
              <span className="lm-stat-label">Requests</span>
              <span className="lm-stat-value">{license.request_count}</span>
            </div>
            <div className="lm-stat">
              <span className="lm-stat-label">Renewals</span>
              <span className="lm-stat-value">{license.renewal_count || 0}</span>
            </div>
            <div className="lm-stat">
              <span className="lm-stat-label">Last IP</span>
              <span className="lm-stat-value mono" title={license.last_request_ip || ''}>
                {license.last_request_ip || '—'}
              </span>
            </div>
          </div>

          <div className="lm-keys">
            <div className="lm-key-row">
              <span className="lm-key-label">Fingerprint</span>
              <span className="lm-key-text" title={license.fingerprint}>
                {truncate(license.fingerprint || 'Not synced')}
              </span>
              <button
                type="button"
                className="lm-copy-btn"
                onClick={() => copyToClipboard(license.fingerprint || '', 'Fingerprint')}
              >
                <Copy size={11} /> Copy
              </button>
            </div>
            <div className="lm-key-row">
              <span className="lm-key-label">License key</span>
              <span className="lm-key-text" title={license.license_key}>
                {license.license_key ? truncate(license.license_key) : 'Unavailable'}
              </span>
              {license.license_key && (
                <button
                  type="button"
                  className="lm-copy-btn"
                  onClick={() => copyToClipboard(license.license_key, 'License key')}
                >
                  <Copy size={11} /> Copy
                </button>
              )}
            </div>
          </div>

          {recentHistory.length > 0 && (
            <>
              <h3 className="lm-activity-title">Recent activity</h3>
              <div className="lm-activity-list">
                {recentHistory.map((req, idx) => (
                  <div key={idx} className="lm-activity-item">
                    <span>Handshake · {req.ip || 'unknown'}</span>
                    <span>{dashboardApi.formatRelativeTime(req.timestamp)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LicenseModal;
